import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { API_URL, IMAGE_BASE_URL } from "../../constant";
import "./CaptainTCCard.css";
import fivePoundCardImg from "../../images/captain-tc/5PoundCard.jpg";
import promoTopCoinIcon from "../../images/shapes/tc-units-wallet 2.png";
import promoCashIcon from "../../images/shapes/tc-cash-wallet 1.png";
import oldbank from "../../images/shapes/bank-pound-icon2 4.png";

function CaptainTCCard() {
  const outletContext = useOutletContext() || {};
  const { userData } = outletContext;

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [seniorWallet, setSeniorWallet] = useState(null);
  const [miniWallet, setMiniWallet] = useState(null);
  const [miniWalletPage, setMiniWalletPage] = useState(1);
  const miniWalletItemsPerPage = 5;

  // Promo wallet balances
  const [extraCardBalance, setExtraCardBalance] = useState(0);
  const [bonusCardBalance, setBonusCardBalance] = useState(0);
  const [unclaimedMilestones, setUnclaimedMilestones] = useState(0);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  // History state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [selectedSbcRef, setSelectedSbcRef] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }, []);

  const fetchCardStatus = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      const response = await fetch(`${API_URL}/api/customer/captain-tc-cards`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.cards) {
          setCards(data.cards || []);
        }
        // Set promo wallet balances
        if (data.promo_wallet) {
          setExtraCardBalance(data.promo_wallet.extra_card_balance || 0);
          setBonusCardBalance(
            ((data.promo_wallet.total_extra_card_balance * 5) /
              data.promo_wallet.total_extra_card_balance) *
              data.promo_wallet.extra_card_balance || 0,
          );
          setUnclaimedMilestones(data.promo_wallet.unclaimed_milestones || 0);
          setHasPendingRequest(data.promo_wallet.has_pending_request || false);
        }

        if (data.senior_wallet) {
          setSeniorWallet(data.senior_wallet);
        }

        if (data.mini_wallet) {
          setMiniWallet(data.mini_wallet);
        }
      }
    } catch (err) {
      console.warn("Captain TC Cards API not available:", err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const handleClaimCashback = async () => {
    if (hasPendingRequest || unclaimedMilestones <= 0) return;

    try {
      setClaimLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_URL}/api/customer/captain-tc-cards/claim-cashback`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message || "Cash back claim submitted successfully.");
        setHasPendingRequest(true);
        // Reduce unclaimed visually immediately to avoid double clicks
        setUnclaimedMilestones(0);
      } else {
        alert(data.message || "Failed to submit claim.");
      }
    } catch (err) {
      console.error("Error claiming cash back:", err);
      alert("An error occurred during submission.");
    } finally {
      setClaimLoading(false);
    }
  };

  const fetchHistory = useCallback(
    async (page = 1, limit = 5) => {
      try {
        setHistoryLoading(true);
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const response = await fetch(
          `${API_URL}/api/customer/captain-tc-cards/history?page=${page}&per_page=${limit}`,
          {
            headers: getAuthHeaders(),
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.history) {
            setHistory(data.history.data || []);
            setHistoryPage(data.history.current_page || 1);
            setHistoryTotalPages(data.history.last_page || 1);
          }
        }
      } catch (err) {
        console.warn("Captain TC Cards history API error:", err.message);
      } finally {
        setHistoryLoading(false);
      }
    },
    [getAuthHeaders],
  );

  const toggleHistoryView = () => {
    const newShowFull = !showFullHistory;
    setShowFullHistory(newShowFull);
    fetchHistory(1, newShowFull ? 10 : 5);
  };

  const handleCopySbcRef = async (ref) => {
    try {
      await navigator.clipboard.writeText(ref);
      alert("SBC REF copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  useEffect(() => {
    fetchCardStatus();
    fetchHistory(1, 5);
  }, [fetchCardStatus, fetchHistory]);

  if (loading) {
    return (
      <div className="captain-tc-wrapper">
        <div className="captain-tc-loading">
          <div className="captain-tc-spinner"></div>
          {/* <p style={{ marginTop: "15px", color: "#666", fontWeight: "500" }}>Loading...</p> */}
        </div>
      </div>
    );
  }

  return (
    <div className="captain-tc-wrapper">
      {/* Card Tiers */}
      <div className="captain-tc-cards-grid">
        {cards.filter(c => c.purchased).length > 0 ? (
          cards.filter(c => c.purchased).map((card) => (
            <div key={card.id || card.tier} className="captain-tc-card-item">
            <div className="captain-tc-card-owned-badge">
              <span className={card.purchased ? "badge-yes" : "badge-no"}>
                Owned: {card.purchased ? "Yes" : "No"}
              </span>
            </div>
            <div className="captain-tc-card-content">
              <div className="captain-tc-card-img-wrap">
                <img
                  src={
                    card.image
                      ? card.image.startsWith("http")
                        ? card.image
                        : `${API_URL}/storage/${card.image}`
                      : fivePoundCardImg
                  }
                  alt={`${card.name} Captain TC Card`}
                  className="captain-tc-actual-card-img"
                  style={{ width: "92px", borderRadius: "8px" }}
                />
              </div>
              <div className="captain-tc-card-details">
                <div className="captain-tc-card-title">Captain TC Card</div>
                <div className="captain-tc-card-edition">{card.name}</div>
                <div className="captain-tc-card-serial">
                  Serial:{" "}
                  {card.purchased && card.serial ? `${card.serial}` : "—"}
                </div>
                {card.purchased && card.expiry_date && (
                  <div className="captain-tc-card-expiry" style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
                    Expiry: {new Date(card.expiry_date).toLocaleDateString("en-GB")}
                  </div>
                )}
                <div className="captain-tc-card-price">
                  {card.price}
                </div>
              </div>
            </div>
          </div>
        ))
        ) : (
          // <div style={{ padding: "20px", textAlign: "center", width: "100%", color: "#666" }}>
          //   You do not currently own any active Captain TC cards.
          // </div>
          console.log("No active Captain TC cards owned.")
        )}
      </div>


      {/* TopCoin Reward Promotion Section */}
      <div className="reward-promo-container">
        <div className="reward-promo-header">
          <div className="reward-promo-title">
            <h3>Captain TC Reward Card Purchase Details</h3>
          </div>
          <button className="view-history-btn" onClick={toggleHistoryView}>
            <i className="fa fa-history" style={{ marginRight: "4px" }}></i>{" "}
            {showFullHistory ? "Show Less" : "View Full History"}
          </button>
        </div>
        <div className="reward-promo-table-wrap">
          <table className="reward-promo-table">
            <thead>
              <tr>
                <th>NO</th>
                <th>SERIAL</th>
                <th>PRICE</th>
                <th>DATE</th>
                <th>TOPCOIN EARNED</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "15px" }}
                  >
                    Loading history...
                  </td>
                </tr>
              ) : history.length > 0 ? (
                history.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(historyPage - 1) * 10 + index + 1}</td>
                    <td>{item.invoice_no || "—"}</td>
                    <td>
                      £{parseFloat(item.transaction_total || 0).toFixed(2)}
                    </td>
                    <td>
                      {new Date(
                        item.transaction_datetime || item.created_at,
                      ).toLocaleDateString("en-GB")}
                    </td>
                    <td>
                      {(() => {
                        const total = parseFloat(item.transaction_total || 0);
                        if (Math.abs(total - 2.5) < 0.01) return 1;
                        if (Math.abs(total - 5) < 0.01) return 2;
                        if (Math.abs(total - 10) < 0.01) return 4;
                        if (Math.abs(total - 25) < 0.01) return 8;
                        // Fallback to proportional calculation if multiple combinations
                        // e.g., if total is 15, it might be 10+5, so 4+2=6.
                        // But since we don't have line items here, we'll try to estimate or just return a default
                        return item.awarding_topcoin || 0;
                      })()}
                    </td>
                    {/* <td>
                      {item.sbc_ref ? (
                        <button
                          onClick={() => setSelectedSbcRef(item.sbc_ref)}
                          style={{
                            padding: "2px 10px",
                            fontSize: "12px",
                            background: "#001964",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          View
                        </button>
                      ) : (
                        "—"
                      )}
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "15px" }}
                  >
                    No history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {showFullHistory && historyTotalPages > 1 && (
          <div
            className="pagination-container"
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "15px",
              gap: "10px",
              flexDirection: "row",
            }}
          >
            <button
              disabled={historyPage === 1}
              onClick={() => fetchHistory(historyPage - 1, 10)}
              className="pagination-btn"
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: historyPage === 1 ? "#eee" : "#fff",
                cursor: historyPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Prev
            </button>
            <span
              style={{ alignSelf: "center", fontSize: "14px", color: "#555" }}
            >
              Page {historyPage} of {historyTotalPages}
            </span>
            <button
              disabled={historyPage === historyTotalPages}
              onClick={() => fetchHistory(historyPage + 1, 10)}
              className="pagination-btn"
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: historyPage === historyTotalPages ? "#eee" : "#fff",
                cursor:
                  historyPage === historyTotalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Promo Wallets Section */}
      <div className="promo-wallets-grid">
        <div className="promo-wallet-card">
          <div className="promo-wallet-icon-wrap blue-bg">
            <img src={promoTopCoinIcon} alt="TopCoin Promo Wallet" />
          </div>
          <div className="promo-wallet-text">
            <div className="promo-wallet-label">
              Captain TC Promotion TopCoin
            </div>
            <div className="promo-wallet-val blue-text">
              {extraCardBalance.toLocaleString()} TC
            </div>
          </div>
        </div>

        <div
          className="promo-wallet-card"
          style={{
            backgroundColor: "#fafafa",
            borderColor: "#e0e0e0",
            opacity: 0.7,
          }}
        >
          <div
            className="promo-wallet-icon-wrap"
            style={{ backgroundColor: "#e0e0e0" }}
          >
            <img src={oldbank} alt="Cash Promo Wallet" />
          </div>
          <div className="promo-wallet-text">
            <div className="promo-wallet-label" style={{ color: "#9e9e9e" }}>
              Withdrawable
            </div>
            <div className="promo-wallet-val" style={{ color: "#9e9e9e" }}>
              {/* {(bonusCardBalance).toFixed(0)} TC */}
              {`£ ${(0).toFixed(2)}`}
            </div>
          </div>
        </div>

        {/* <div className="promo-wallet-card">
                    <div className="promo-wallet-icon-wrap orange-bg">
                        <img src={promoCashIcon} alt="Cash Promo Wallet" />
                    </div>
                    <div className="promo-wallet-text">
                        <div className="promo-wallet-label">Captain TC Promotional Cash Wallet</div>
                        <div className="promo-wallet-val orange-text">
                            0 TC
                        </div>
                    </div>
                </div> */}
      </div>

      {/* Claim Cash Back Section */}
      {(unclaimedMilestones > 0 || hasPendingRequest) && (
        <div
          className="captain-tc-claim-section"
          style={{
            marginTop: "20px",
            padding: "20px",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          <h3 style={{ marginBottom: "10px", color: "#333" }}>
            Claim Cash Back
          </h3>
          <p style={{ fontSize: "14px", color: "#555", marginBottom: "15px" }}>
            Rules: You need at least 250 TopCoins in extra card balance to
            claim. Each card covers 20 claims. Once requested, the Account Admin
            will review and approve your bonus card balance.
          </p>
          <button
            onClick={handleClaimCashback}
            disabled={claimLoading || hasPendingRequest}
            style={{
              padding: "10px 20px",
              backgroundColor: hasPendingRequest ? "#ccc" : "#001964",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: hasPendingRequest ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
          >
            {claimLoading
              ? "Submitting..."
              : hasPendingRequest
                ? "Cash Back Requested (Pending Approval)"
                : `Claim Cash Back (${unclaimedMilestones} available)`}
          </button>
        </div>
      )}

      {/* SBC REF Modal */}
      {selectedSbcRef && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "25px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              position: "relative",
              wordBreak: "break-all",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            }}
          >
            <h4 style={{ marginTop: 0, color: "#333" }}>
              SBC Reference Number
            </h4>
            <p
              style={{
                margin: "15px 0",
                padding: "12px",
                background: "#f5f5f5",
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
                color: "#555",
                fontSize: "14px",
              }}
            >
              {selectedSbcRef}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => handleCopySbcRef(selectedSbcRef)}
                style={{
                  padding: "8px 15px",
                  background: "#001964",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: "bold",
                }}
              >
                <i className="fa fa-copy"></i> Copy
              </button>
              <button
                onClick={() => setSelectedSbcRef(null)}
                style={{
                  padding: "8px 15px",
                  background: "#ccc",
                  color: "#333",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaptainTCCard;
